import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Grid,
  Chip,
  Stack,
  Divider,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy"; // 👈 new icon
import { AnimatePresence, motion } from "framer-motion";
import useMediaQuery from "@mui/material/useMediaQuery";

const MotionBox = motion(Box);

const Field = ({ label, value, emphasize = false }) => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <Stack spacing={0.5}>
      <Box sx={{ fontSize: 12, opacity: 0.7 }}>{label}</Box>
      <Box
        sx={{
          fontWeight: emphasize ? 600 : 500,
          wordBreak: "break-word",
          bgcolor: emphasize ? "action.hover" : "transparent",
          px: emphasize ? 1 : 0,
          py: emphasize ? 0.5 : 0,
          borderRadius: emphasize ? 1 : 0,
        }}
      >
        {value ?? "—"}
      </Box>
    </Stack>
  </Grid>
);

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

export default function EnquiryDetailsDialog({
  open,
  onClose,
  data,
  onApprove,
  onReject,
}) {
  const fullScreen = useMediaQuery("(max-width:600px)");
  const leadSourceName = data?.lead_source?.name ?? null;
  const status = data?.status ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
        },
      }}
    >
      <DialogTitle sx={{ pr: 7 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Box sx={{ fontWeight: 700, fontSize: { xs: 18, sm: 20 } }}>
            {data?.name ?? "Details"}
          </Box>

          {/* Registration No + Copy */}
          {data?.registration_no && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={data.registration_no}
                variant="outlined"
              />
              <Tooltip title="Copy Registration No">
                <IconButton
                  size="small"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        String(data.registration_no)
                      );
                    } catch (e) {}
                  }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {status && (
            <Chip
              size="small"
              label={String(status).toUpperCase()}
              color={
                status === "approved"
                  ? "success"
                  : status === "pending"
                  ? "warning"
                  : status === "rejected"
                  ? "error"
                  : "default"
              }
            />
          )}
        </Stack>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "red",
            fontWeight: "bold",
          }}
        >
          <CloseIcon sx={{ fontWeight: "bold" }} />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <AnimatePresence mode="wait">
          {open && (
            <MotionBox
              key={data?.id ?? "empty"}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
            >
              <Grid container spacing={2}>
                <Field label="Name" value={data?.name} emphasize />
                <Field label="Father's Name" value={data?.father_name} />
                <Field label="DOB" value={formatDate(data?.dob)} />
                <Field label="Marital Status" value={data?.marital_status} />

                <Field label="Mobile" value={data?.mobile} emphasize />
                <Field label="Email" value={data?.email} />

                <Field label="Address Line 1" value={data?.address_line_1} />
                <Field label="Address Line 2" value={data?.address_line_2} />
                <Field label="State" value={data?.state} />
                <Field label="Country" value={data?.country} />
                <Field label="Pincode" value={data?.pincode} />

                <Field label="Aadhaar No" value={data?.aadhaar_no} />
                <Field label="Gotra" value={data?.gotra} />
                <Field label="Kuldevi" value={data?.kuldevi} />

                <Field
                  label="Created At"
                  value={formatDate(data?.created_at)}
                />
                <Field label="Remarks" value={data?.remarks} />
              </Grid>

              {/* Bottom Approve / Reject Buttons */}
              <Box
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    onApprove?.(data);
                    onClose?.(); // 👈 dialog close bhi hoga
                  }}
                  disabled={
                    data.status === "approved" || data.status === "rejected"
                  }
                >
                  Approve
                </Button>

                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    onReject?.(data);
                    onClose?.(); // 👈 dialog close bhi hoga
                  }}
                  disabled={
                    data.status === "approved" || data.status === "rejected"
                  }
                >
                  Reject
                </Button>
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
